import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useMousePositionAsTrigger } from 'react-laag';
import styled from 'styled-components';
import { ActionRegistry } from '@packages/table-builder/registry/ActionRegistry';
import type {
  ActionExecutionContext,
  ComposeModalConfig,
  ComposePopoverConfig,
} from '@packages/table-builder';
import { ControlRenderer } from '@packages/compose/core';
import PopoverModalComponent from '@/components/modal/popoverModal';
import { PopoverContentWrapper } from '@/components/modal/modal.styled';

const PopoverBody = styled.div<{ $maxWidthPx?: number; $width?: string }>`
  box-sizing: border-box;
  width: ${({ $width }) => $width ?? '40vw'};
  max-width: ${({ $maxWidthPx }) => ($maxWidthPx != null ? `${$maxWidthPx}px` : '850px')};
  padding: 16px;
`;

type ClientAnchor = { clientX: number; clientY: number };

type PopoverSession = {
  actionId: string;
  context: ActionExecutionContext;
  instanceKey: string;
};

type PopoverState = {
  session: PopoverSession | null;
};

type PopoverAction =
  | { type: 'OPEN'; session: PopoverSession }
  | { type: 'CLOSE' };

const initialState: PopoverState = { session: null };

function popoverReducer(state: PopoverState, action: PopoverAction): PopoverState {
  switch (action.type) {
    case 'OPEN':
      return { session: action.session };
    case 'CLOSE':
      return { session: null };
    default: {
      const _exhaustive: never = action;
      return state;
    }
  }
}

function readClientAnchor(meta: Record<string, unknown> | undefined): ClientAnchor | null {
  const anchor = meta?.clientAnchor;
  if (anchor == null || typeof anchor !== 'object') return null;
  const { clientX, clientY } = anchor as ClientAnchor;
  if (typeof clientX !== 'number' || typeof clientY !== 'number') return null;
  return { clientX, clientY };
}

function readComposePopover(meta: Record<string, unknown> | undefined): ComposePopoverConfig {
  const raw = meta?.composePopover;
  if (raw == null || typeof raw !== 'object') return {};
  return raw as ComposePopoverConfig;
}

function buildControlRendererMeta(
  context: ActionExecutionContext,
): Record<string, unknown> {
  const meta = (context.meta ?? {}) as Record<string, unknown>;
  const composeMeta = (meta.composeMeta ?? {}) as Partial<ComposeModalConfig>;
  return {
    mode: composeMeta.mode ?? 'view',
    entity: composeMeta.entity,
    semanticId: composeMeta.semanticId,
  };
}

/**
 * App-level handler for schema-driven compose popovers (OPEN_COMPOSE_POPOVER).
 * Mounted once in CustomerAppShell alongside ActionIntentHandler.
 */
export function ComposePopoverHandler() {
  const [state, dispatch] = useReducer(popoverReducer, initialState);
  const {
    resetMousePosition,
    handleMouseEvent,
    trigger,
  } = useMousePositionAsTrigger();

  const handleClose = useCallback(() => {
    resetMousePosition();
    dispatch({ type: 'CLOSE' });
  }, [resetMousePosition]);

  const handleMouseEventRef = useRef(handleMouseEvent);
  handleMouseEventRef.current = handleMouseEvent;

  const pendingAnchorRef = useRef<ClientAnchor | null>(null);

  const applyAnchor = useCallback((anchor: ClientAnchor) => {
    handleMouseEventRef.current({
      clientX: anchor.clientX,
      clientY: anchor.clientY,
      pageX: anchor.clientX,
      pageY: anchor.clientY,
      preventDefault: () => {},
    } as ReactMouseEvent);
  }, []);

  useEffect(() => {
    return ActionRegistry.onIntent((intent) => {
      if (intent.type !== 'OPEN_COMPOSE_POPOVER') return;

      const anchor = readClientAnchor(intent.context.meta as Record<string, unknown> | undefined);
      pendingAnchorRef.current = anchor;
      if (anchor) {
        applyAnchor(anchor);
      }

      const session: PopoverSession = {
        actionId: intent.actionId,
        context: intent.context,
        instanceKey: `${intent.actionId}-${intent.context.pk}-${Date.now()}`,
      };

      // Defer open until after the opening click finishes (laag document capture listener).
      queueMicrotask(() => {
        dispatch({ type: 'OPEN', session });
      });
    });
  }, [applyAnchor]);

  const session = state.session;

  useLayoutEffect(() => {
    if (session == null) return;
    const anchor =
      readClientAnchor(session.context.meta as Record<string, unknown> | undefined)
      ?? pendingAnchorRef.current;
    if (anchor) {
      applyAnchor(anchor);
    }
  }, [session, applyAnchor]);
  const panelConfig = useMemo(
    () => readComposePopover(session?.context.meta as Record<string, unknown> | undefined),
    [session?.context.meta],
  );

  const params = useMemo(() => {
    if (!session) return { id: '' };
    return { id: session.context.pk, ...(session.context.params ?? {}) };
  }, [session]);

  const controlMeta = useMemo(() => {
    if (!session) return { mode: 'view' as const };
    return buildControlRendererMeta(session.context);
  }, [session]);

  const isPopoverOpen = session != null;

  return (
    <PopoverModalComponent
      hasMousePosition={isPopoverOpen}
      trigger={trigger}
      popoverClassName={panelConfig.panelClassName}
      resetMousePosition={handleClose}
      id='composePopoverHandler'
    >
      {session ? (
        <PopoverContentWrapper className={panelConfig.panelClassName}>
          <PopoverBody $maxWidthPx={panelConfig.maxWidthPx} $width={panelConfig.width}>
            <ControlRenderer
              key={session.instanceKey}
              actionId={session.actionId}
              params={params}
              meta={controlMeta}
            />
          </PopoverBody>
        </PopoverContentWrapper>
      ) : (
        <span aria-hidden style={{ display: 'none' }} />
      )}
    </PopoverModalComponent>
  );
}
